import React, { useEffect, useState } from "react";
import { Users, UserCheck, DollarSign, Award, Activity, Trophy, Layers } from "lucide-react";
import { BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import api from "../lib/api";
import { StatCard, PageLoader, ChartTooltip } from "../components/ui";
import { money, statusMeta } from "../lib/constants";

const COLORS = ["#6366f1", "#10b981", "#f59e0b", "#ef4444", "#06b6d4", "#8b5cf6", "#ec4899", "#84cc16", "#f97316", "#14b8a6", "#a855f7", "#64748b", "#0ea5e9"];

function Panel({ title, icon: Icon, children, className = "" }) {
  return (
    <div className={`card p-6 ${className}`}>
      <h3 className="font-semibold font-display text-slate-900 dark:text-white mb-5 flex items-center gap-2">
        {Icon && <Icon className="w-4 h-4 text-indigo-500" />} {title}
      </h3>
      {children}
    </div>
  );
}

function Donut({ data }) {
  const filtered = data.filter((d) => d.value > 0);
  return (
    <div>
      <ResponsiveContainer width="100%" height={240}>
        <PieChart>
          <Pie data={filtered} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={55} outerRadius={95} paddingAngle={2}>
            {filtered.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
          </Pie>
          <Tooltip content={<ChartTooltip />} />
        </PieChart>
      </ResponsiveContainer>
      <div className="flex flex-wrap gap-x-4 gap-y-1.5 mt-3 justify-center">
        {data.map((d, i) => (
          <span key={d.name} className="flex items-center gap-1.5 text-xs text-slate-600 dark:text-slate-300">
            <span className="w-2.5 h-2.5 rounded-full" style={{ background: COLORS[i % COLORS.length] }} />
            {d.name}: <b>{d.value.toLocaleString()}</b>
          </span>
        ))}
      </div>
    </div>
  );
}

export default function Analytics() {
  const [a, setA] = useState(null);
  useEffect(() => { api.get("/analytics", { params: { days: 30 } }).then((r) => setA(r.data)); }, []);
  if (!a) return <PageLoader />;

  const t = a.totals;
  const leadDist = Object.entries(a.lead_distribution).map(([k, v]) => ({ name: statusMeta("lead", k).label, value: v }));
  const clientDist = Object.entries(a.client_distribution).map(([k, v]) => ({ name: statusMeta("client", k).label, value: v }));
  const perf = [...a.agents].sort((x, y) => y.deposits - x.deposits);
  const byLeads = [...a.agents].filter((x) => x.leads > 0).sort((x, y) => y.leads - x.leads);
  const leadDistByAgent = byLeads.map((x) => ({ name: x.name, value: x.leads }));
  const maxLeads = Math.max(1, ...byLeads.map((x) => x.leads));
  const leaderboard = [...a.agents].sort((x, y) => y.deposits - x.deposits || y.clients - x.clients);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold font-display tracking-tight text-slate-900 dark:text-white">Analytics</h1>
        <p className="text-sm text-slate-500 dark:text-slate-400">Overview of all agent performance</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard label="Total Leads" value={t.total_leads.toLocaleString()} icon={Users} testid="an-leads" />
        <StatCard label="Active Clients" value={t.active_clients.toLocaleString()} icon={UserCheck} accent="text-emerald-600 dark:text-emerald-400" testid="an-clients" />
        <StatCard label="Total Agents" value={t.total_agents} icon={Award} accent="text-amber-600 dark:text-amber-400" testid="an-agents" />
        <StatCard label="Total Deposits" value={money(t.total_deposits)} icon={DollarSign} accent="text-cyan-600 dark:text-cyan-400" testid="an-deposits" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Panel title="Lead Status Distribution" icon={Layers}><Donut data={leadDist} /></Panel>
        <Panel title="Client Status Distribution" icon={Layers}><Donut data={clientDist} /></Panel>
      </div>

      <Panel title="Agent Performance (Deposits)" icon={DollarSign}>
        <ResponsiveContainer width="100%" height={320}>
          <BarChart data={perf} margin={{ bottom: 60 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#94a3b833" />
            <XAxis dataKey="name" tick={{ fontSize: 10 }} stroke="#94a3b8" angle={-35} textAnchor="end" interval={0} height={70} />
            <YAxis tick={{ fontSize: 11 }} stroke="#94a3b8" />
            <Tooltip content={<ChartTooltip formatter={(v) => money(v)} />} />
            <Bar dataKey="deposits" radius={[4, 4, 0, 0]} fill="#10b981" />
          </BarChart>
        </ResponsiveContainer>
      </Panel>

      <Panel title="Agent Activity (last 30 days)" icon={Activity}>
        {a.agent_activity.length === 0 ? (
          <div className="h-32 flex items-center justify-center text-slate-400">No activity in this period</div>
        ) : (
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={a.agent_activity} margin={{ bottom: 60 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#94a3b833" />
              <XAxis dataKey="name" tick={{ fontSize: 10 }} stroke="#94a3b8" angle={-35} textAnchor="end" interval={0} height={70} />
              <YAxis tick={{ fontSize: 11 }} stroke="#94a3b8" />
              <Tooltip content={<ChartTooltip />} />
              <Bar dataKey="count" radius={[4, 4, 0, 0]} fill="#6366f1" name="Events" />
            </BarChart>
          </ResponsiveContainer>
        )}
      </Panel>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Panel title="Lead Distribution by Agent" icon={Users}><Donut data={leadDistByAgent} /></Panel>
        <Panel title="Agent Workload" icon={Layers}>
          <div className="space-y-3">
            {byLeads.map((x, i) => (
              <div key={x.name}>
                <div className="flex items-center justify-between text-sm mb-1">
                  <span className="font-medium text-slate-700 dark:text-slate-200">{x.name}</span>
                  <span className="text-slate-400 text-xs">{x.leads.toLocaleString()} leads • {x.clients} clients</span>
                </div>
                <div className="h-2 rounded-full bg-slate-100 dark:bg-slate-800 overflow-hidden">
                  <div className="h-full rounded-full" style={{ width: `${(x.leads / maxLeads) * 100}%`, background: COLORS[i % COLORS.length] }} />
                </div>
              </div>
            ))}
          </div>
        </Panel>
      </div>

      <Panel title="Agent Leaderboard" icon={Trophy}>
        <div className="space-y-2">
          {leaderboard.map((x, i) => (
            <div key={x.name} className="flex items-center gap-4 rounded-lg border border-slate-100 dark:border-slate-800 px-4 py-3" data-testid={`leaderboard-${i + 1}`}>
              <div className={`h-8 w-8 shrink-0 rounded-full flex items-center justify-center text-sm font-bold ${i === 0 ? "bg-amber-100 text-amber-600 dark:bg-amber-500/15" : i === 1 ? "bg-slate-200 text-slate-600 dark:bg-slate-700 dark:text-slate-200" : i === 2 ? "bg-orange-100 text-orange-600 dark:bg-orange-500/15" : "bg-slate-100 text-slate-400 dark:bg-slate-800"}`}>{i + 1}</div>
              <div className="min-w-0 flex-1">
                <div className="font-semibold text-slate-900 dark:text-white">{x.name}</div>
                <div className="text-xs text-slate-400">{x.leads.toLocaleString()} leads • {x.clients} clients • {x.conversions} conversions</div>
              </div>
              <div className="text-right">
                <div className="font-bold font-display text-emerald-600 dark:text-emerald-400">{money(x.deposits)}</div>
                <div className="text-[11px] text-slate-400">Total Deposits</div>
              </div>
            </div>
          ))}
        </div>
      </Panel>
    </div>
  );
}
